import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
    Activity, ArrowLeft, Bot, Building, Check, ChevronRight, ChevronsUpDown, Flag, LayoutGrid, LogOut, Search, Settings, Settings2, ShieldCheck, SlidersHorizontal, Users,
    type LucideIcon,
} from 'lucide-react'
import type { Me } from '@/contract'
import { clientCounts } from '@/features/admin/embaixadores/embaixadores-logic'
import { prospectsQuery } from '@/features/admin/rede/api'
import { counts } from '@/features/admin/rede/logic'
import { adminMeQuery } from '@/features/admin/shared/api'
import { can, type AdminRole } from '@/features/admin/shared/permissions'
import { splitPath } from '@/features/admin/hoje/hoje-logic'
import { CONFIG_SECTIONS } from '@/features/erp/config/registry'
import type { FrenteKey } from '@/features/erp/contract'
import { useFrenteNav } from '@/features/erp/frente-state'
import { frenteEntry, frenteNav } from '@/features/erp/frentes'
import { EXPERIENCE_ICONS } from '@/features/erp/ui/experience-icons'
import { FrenteSwitcher } from '@/features/erp/ui/FrenteSwitcher'
import { panelApi } from '@/lib/api'
import { overviewQuery } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { MAIN_PAGES_NAV } from './nav'
import { configGroups, experienceTree, menuItemActive, staffMenu, treeActivePath, treeViews, type XpTreeItem } from './staff-nav'
import { markFreshNav } from './trail-store'
import { Francisco } from '@/components/francisco/Francisco'
import { openCommandPalette, paletteShortcutLabel } from './CommandPalette'

/* Sidebar of the whole panel (COG-005 + COG-009 + ERP frentes), in the brand look (BALANCE.md): white panel, menta
   tint on the active item, Montserrat. Two spaces:
   - Staff (/staff/*): the current frente on top (switcher when the profile opens more than one), then one item per
     experience the frente sees (Sponsor, Staff, Corporate…; clicking opens the experience overview; its screens become
     tabs above the content, AppShell). An experience with a tree (EXPERIENCE_TREES, all of them, e.g. Sponsor → Hoje,
     A IA fez, Carteira (the tabs of its overview, ?tab=), Sponsor App → Doadores…; Staff → Meu dia, Minha jornada…) shows it nested under its item instead
     of the tabs, only in the expanded sidebar (the rail and the phone bar keep the plain item; AppShell and the overview
     keep the tabs there). Switching frente lands on the frente's entry experience.
   - Configurações (admin only; the old Admin space + /configuracoes/*): grouped, collapsible themes (shadcn sidebar
     pattern; the theme of the current screen opens by itself, the others keep what the person chose in localStorage).
   Brand switches spaces (admin only). Footer = Denúncia (Staff) + the person with "Sair". Collapsible to the icon rail
   (localStorage). Phones (≤760px): the cf-side* classes turn it into the top bar (styles/main.css), labels hidden. */

/** Contador do menu (pendências da tela): amarelo suave — fundo amarelo-claro, número marrom, menor e sem negrito
    (pedido de 2026-11-03: o amarelo cheio poluía o menu). */
const BADGE = 'tabular min-w-[18px] rounded-full bg-[var(--yellow-bg)] px-1.5 text-center text-[11px] leading-[17px] font-medium text-[var(--yellow-ink)]'

type Item = { to: string; label: string; title?: string; icon: LucideIcon; badge?: number; live?: boolean }
type Group = { label: string; items: Item[] }

/** Icon of each theme in the collapsible sections (Configurações). */
const GROUP_ICONS: Record<string, LucideIcon> = {
    Operação: Activity, Agentes: Bot, Sistema: Settings2,
    Pessoas: Users, ERP: SlidersHorizontal, Segurança: ShieldCheck, Organização: Building,
}

const CONFIG_ITEM_ICONS: Record<string, LucideIcon> = { Pessoas: Users, ERP: SlidersHorizontal, Segurança: ShieldCheck, Organização: Building }

/** Configurações (admin only): the old Admin space (Operação, Agentes, Sistema) + the ERP settings sections. */
function configMenu(pending: number): Group[] {
    const groups: Group[] = []
    for (const p of MAIN_PAGES_NAV) {
        let group = groups.find(g => g.label === p.group)
        if (!group) groups.push(group = { label: p.group, items: [] })
        group.items.push({ to: p.to, label: p.label, title: p.title, icon: p.icon, live: p.live, badge: p.pendingBadge ? pending : undefined })
    }
    for (const g of configGroups(CONFIG_SECTIONS)) groups.push({ label: g.label, items: g.items.map(i => ({ to: i.to, label: i.label, icon: CONFIG_ITEM_ICONS[g.label] ?? Settings2 })) })
    return groups
}

type Badges = Partial<Record<string, number>>
type StaffItem = Item & { key: string; active: (p: string) => boolean; tree: XpTreeItem[] | null }

/** Staff menu of the frente: one item per experience (opens its overview; current while on any of its tabs), with the
    experience tree when it has one. */
function staffItems(nav: Parameters<typeof staffMenu>[0], badges: Badges, role: AdminRole | null): StaffItem[] {
    return staffMenu(nav).map(m => {
        const tree = m.xp ? experienceTree(m.xp, m.views, role) : null
        return {
        key: m.key,
        // With a tree, the item opens its first node (the overview is no longer in the tree outside Staff)
        to: tree?.[0]?.to ?? m.to,
        label: m.label,
        icon: m.xp ? EXPERIENCE_ICONS[m.xp] : LayoutGrid,
        badge: m.views.reduce((t, v) => t + (badges[v.key] ?? 0), 0) || undefined,
        active: (p: string) => menuItemActive(m, p),
        tree,
        }
    })
}

/** Counts of the Rede screens (régua, aprovação do cliente, não aprovados), by ERP view key. Same queries the screens
    use, so they share the cache; only asked when the frente menu has those screens. */
function useRedeBadges(role: AdminRole | null, keys: readonly string[]): Partial<Record<string, number>> {
    const has = (prefix: string) => keys.some(k => k.startsWith(prefix))
    const ambassadors = !!role && can(role, 'embaixadores') && has('emb-')
    const corporate = !!role && can(role, 'parceiros') && has('emp-')
    const churches = !!role && can(role, 'parceiros') && has('igr-')
    const amb = useQuery({ ...prospectsQuery('ambassador'), enabled: ambassadors }).data?.items
    const corp = useQuery({ ...prospectsQuery('corporate'), enabled: corporate }).data?.items
    const church = useQuery({ ...prospectsQuery('church'), enabled: churches }).data?.items
    const out: Partial<Record<string, number>> = {}
    const journey = (prefix: string, items: typeof amb) => {
        if (!items) return
        const c = counts(items)
        out[`${prefix}-regua`] = c.inJourney
        out[`${prefix}-naoaprov`] = c.rejected
    }
    journey('emb', amb)
    journey('emp', corp)
    journey('igr', church)
    if (amb) out['emb-aprovacao'] = clientCounts(amb).pend
    return out
}

const COLLAPSE_KEY = 'cf-sidebar-collapsed'

function readCollapsed(): boolean {
    try {
        return window.localStorage.getItem(COLLAPSE_KEY) === '1'
    } catch {
        return false
    }
}

/** Collapsed state shared by the sidebar and the toggle in the content header (one source: localStorage + event). */
export function useSidebarCollapsed(): [boolean, (v: boolean) => void] {
    const [collapsed, setCollapsed] = useState(readCollapsed)
    useEffect(() => {
        const sync = () => setCollapsed(readCollapsed())
        window.addEventListener('cf-sidebar', sync)
        return () => window.removeEventListener('cf-sidebar', sync)
    }, [])
    const set = (v: boolean) => {
        try {
            window.localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0')
        } catch {
            /* private mode: keep it for this page only */
        }
        setCollapsed(v)
        window.dispatchEvent(new Event('cf-sidebar'))
    }
    return [collapsed, set]
}

const openKey = (workspace: string) => `cf-sidebar-open:${workspace}`

function readOpen(workspace: string): string[] | null {
    try {
        const raw = window.localStorage.getItem(openKey(workspace))
        const parsed: unknown = raw ? JSON.parse(raw) : null
        return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null
    } catch {
        return null
    }
}

/** Open themes of the expanded sidebar. First visit: only the theme of the current screen. */
function useOpenGroups(workspace: string, activeGroup: string | null): [Set<string>, (label: string) => void] {
    const [open, setOpen] = useState<Set<string>>(() => new Set(readOpen(workspace) ?? (activeGroup ? [activeGroup] : [])))
    useEffect(() => {
        setOpen(new Set(readOpen(workspace) ?? (activeGroup ? [activeGroup] : [])))
    }, [workspace])
    // Navigating to a screen opens its theme
    useEffect(() => {
        if (activeGroup) setOpen(prev => (prev.has(activeGroup) ? prev : new Set([...prev, activeGroup])))
    }, [activeGroup])
    const toggle = (label: string) => setOpen(prev => {
        const next = new Set(prev)
        if (next.has(label)) next.delete(label)
        else next.add(label)
        try {
            window.localStorage.setItem(openKey(workspace), JSON.stringify([...next]))
        } catch {
            /* private mode: keep it for this page only */
        }
        return next
    })
    return [open, toggle]
}

const TREE_KEY = 'cf-sidebar-tree'

/** Open nodes of the experience trees (the experience item itself too, by its xp key). The branch of the current screen
    opens by itself; what the person opens or closes stays in localStorage. */
function useOpenNodes(activeIds: readonly string[]): [Set<string>, (id: string, open?: boolean) => void] {
    const [open, setOpen] = useState<Set<string>>(() => {
        try {
            const parsed: unknown = JSON.parse(window.localStorage.getItem(TREE_KEY) ?? 'null')
            if (Array.isArray(parsed)) return new Set([...parsed.filter((x): x is string => typeof x === 'string'), ...activeIds])
        } catch {
            /* private mode or bad value: start from the current branch */
        }
        return new Set(activeIds)
    })
    // Navigating to a screen opens its branch
    const activeKey = activeIds.join('|')
    useEffect(() => {
        if (activeIds.length) setOpen(prev => (activeIds.every(id => prev.has(id)) ? prev : new Set([...prev, ...activeIds])))
    }, [activeKey])
    const set = (id: string, value?: boolean) => setOpen(prev => {
        const on = value ?? !prev.has(id)
        if (on === prev.has(id)) return prev
        const next = new Set(prev)
        if (on) next.add(id)
        else next.delete(id)
        try {
            window.localStorage.setItem(TREE_KEY, JSON.stringify([...next]))
        } catch {
            /* private mode: keep it for this page only */
        }
        return next
    })
    return [open, set]
}

const isActive = (pathname: string, to: string) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`))

/** Small popover anchored to its trigger; closes on Esc and outside click. */
function Popover({ open, onClose, className, children }: { open: boolean; onClose: () => void; className?: string; children: ReactNode }) {
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        const onDown = (e: PointerEvent) => { if (ref.current && !ref.current.parentElement?.contains(e.target as Node)) onClose() }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open, onClose])
    if (!open) return null
    return (
        <div ref={ref} role="menu" className={cn('absolute z-40 flex min-w-[220px] flex-col gap-0.5 rounded-[12px] border border-line bg-panel p-1.5', className)} style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}>
            {children}
        </div>
    )
}

const menuItem = 'flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-[8px] border-0 bg-transparent px-2.5 text-left text-[13.5px] text-fg no-underline hover:bg-[var(--hover)]'

function Brand({ workspace, collapsed, canSwitch }: { workspace: 'main' | 'admin'; collapsed: boolean; canSwitch: boolean }) {
    const [open, setOpen] = useState(false)
    const navigate = useNavigate()
    const go = (to: '/' | '/staff') => {
        setOpen(false)
        void navigate({ to })
    }
    const subtitle = workspace === 'admin' ? 'Staff' : 'Configurações'
    const face = (
        <>
            <span className="grid size-9 flex-none place-items-center overflow-hidden rounded-[10px] bg-tint">
                <Francisco color="g" variant="brand" size={26} follow={false} />
            </span>
            <span className={cn('cf-only-wide min-w-0 flex-1 flex-col leading-[1.25]', collapsed ? 'hidden' : 'flex')}>
                <span className="truncate text-[14px] font-semibold text-fg">ChildFund</span>
                <span className="truncate text-[12px] text-muted">{subtitle}</span>
            </span>
        </>
    )
    // Só o perfil admin troca de espaço (Staff ⇄ Configurações); os outros ficam no Staff
    if (!canSwitch) {
        return (
            <div className={cn('cf-brand flex w-full items-center gap-2.5 p-1.5', collapsed && 'justify-center')} title={collapsed ? `ChildFund · ${subtitle}` : undefined}>
                {face}
            </div>
        )
    }
    return (
        <div className="cf-brand relative w-full">
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                title={collapsed ? `ChildFund · ${subtitle}` : undefined}
                className={cn('flex w-full cursor-pointer items-center gap-2.5 rounded-[12px] border-0 bg-transparent p-1.5 text-left hover:bg-[var(--hover)]', collapsed && 'justify-center')}
            >
                {face}
                {!collapsed && <ChevronsUpDown size={16} className="cf-only-wide flex-none text-muted" aria-hidden="true" />}
            </button>
            <Popover open={open} onClose={() => setOpen(false)} className={cn('top-full mt-1', collapsed ? 'left-0' : 'right-0 left-0')}>
                <span className="px-2.5 pt-1 pb-1.5 text-[12px] text-muted">Trocar de espaço</span>
                {(['admin', 'main'] as const).map(w => (
                    <button key={w} type="button" role="menuitemradio" aria-checked={workspace === w} className={menuItem} onClick={() => go(w === 'main' ? '/' : '/staff')}>
                        <span className="grid size-7 flex-none place-items-center rounded-[8px] border border-line">
                            {w === 'admin' ? <LayoutGrid size={15} aria-hidden="true" /> : <Settings size={15} aria-hidden="true" />}
                        </span>
                        <span className="flex-1">{w === 'admin' ? 'Staff' : 'Configurações'}</span>
                        {workspace === w && <Check size={15} className="text-green" aria-hidden="true" />}
                    </button>
                ))}
            </Popover>
        </div>
    )
}

/** Opens the ⌘K palette. Full-width "Buscar… ⌘K" when expanded; icon only when collapsed and on phones. */
function SearchTrigger({ collapsed }: { collapsed: boolean }) {
    const shortcut = paletteShortcutLabel()
    return (
        <button
            type="button"
            onClick={openCommandPalette}
            aria-label={`Buscar (${shortcut})`}
            title={collapsed ? `Buscar (${shortcut})` : undefined}
            aria-keyshortcuts="Meta+K Control+K"
            className={cn(
                'cf-ri mt-1 flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-[10px] border border-line bg-transparent px-2.5 text-left text-[14px] text-muted hover:bg-[var(--hover)]',
                collapsed && 'justify-center border-transparent px-0',
            )}
        >
            <span className="cf-tile grid size-6 flex-none place-items-center"><Search size={17} strokeWidth={1.8} aria-hidden="true" /></span>
            <span className={cn('cf-only-wide min-w-0 flex-1 truncate', collapsed && 'hidden')}>Buscar…</span>
            {!collapsed && <kbd className="cf-only-wide flex-none rounded-[6px] border border-line px-1.5 font-sans text-[11.5px] leading-[18px] text-muted">{shortcut}</kbd>}
        </button>
    )
}

/** Screen inside an open theme: label only, indented under the guide line (the icon shows only in the phone bar). */
function SubItem({ item, active }: { item: Item; active: boolean }) {
    const Icon = item.icon
    return (
        <Link
            to={item.to as '/'}
            title={item.title}
            aria-current={active ? 'page' : undefined}
            className={cn('cf-ri cf-sub-item relative flex min-h-8 w-full items-center gap-2.5 rounded-[8px] px-2.5 text-[13.5px] no-underline transition-colors', active ? 'font-medium' : 'hover:bg-[var(--hover)]')}
            style={active ? { background: 'var(--tint)', color: 'var(--text)' } : { color: 'var(--muted)' }}
        >
            <span className="cf-tile cf-sub-icon relative hidden size-6 flex-none place-items-center" style={{ color: active ? 'var(--green)' : 'var(--muted)' }}>
                <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
            </span>
            <span className={cn('cf-only-wide min-w-0 flex-1 truncate', active ? 'text-fg' : 'text-fg/90')}>{item.label}</span>
            {!!item.badge && <span className={cn('cf-only-wide', BADGE)}>{item.badge}</span>}
        </Link>
    )
}

/** A theme of the expanded sidebar: header button (icon, name, chevron) + its screens when open. */
function GroupSection({ group, open, onToggle, pathname }: { group: Group; open: boolean; onToggle: () => void; pathname: string }) {
    const Icon = GROUP_ICONS[group.label] ?? LayoutGrid
    const id = `cf-group-${group.label.normalize('NFD').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`
    const hasActive = group.items.some(i => isActive(pathname, i.to))
    const badge = group.items.reduce((t, i) => t + (i.badge ?? 0), 0)
    return (
        <div role="group" aria-label={group.label} className="flex flex-col gap-0.5">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={id}
                onClick={onToggle}
                className="cf-only-wide flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-[10px] border-0 bg-transparent px-2.5 text-left text-[14px] hover:bg-[var(--hover)]"
                style={{ color: hasActive ? 'var(--text)' : 'var(--muted)' }}
            >
                <span className="grid size-6 flex-none place-items-center" style={{ color: hasActive ? 'var(--green)' : 'var(--muted)' }}>
                    <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                </span>
                <span className={cn('min-w-0 flex-1 truncate', hasActive ? 'font-medium text-fg' : 'text-fg/90')}>{group.label}</span>
                {!open && badge > 0 && <span className={BADGE}>{badge}</span>}
                <ChevronRight size={16} aria-hidden="true" className="flex-none text-muted transition-transform duration-150 motion-reduce:transition-none" style={{ transform: open ? 'rotate(90deg)' : undefined }} />
            </button>
            <div id={id} className={cn('cf-sub ml-[22px] flex flex-col gap-0.5 border-l border-line pl-2.5', !open && 'cf-sub-closed')}>
                {group.items.map(item => <SubItem key={item.to} item={item} active={isActive(pathname, item.to)} />)}
            </div>
        </div>
    )
}

const treeBadge = (node: XpTreeItem, badges: Badges) => treeViews(node).reduce((t, v) => t + (badges[v.key] ?? 0), 0)

function Chevron({ open, label, controls, onClick }: { open: boolean; label: string; controls: string; onClick: () => void }) {
    return (
        <button
            type="button"
            aria-expanded={open}
            aria-controls={controls}
            aria-label={open ? `Recolher ${label}` : `Abrir ${label}`}
            onClick={onClick}
            className="cf-only-wide grid size-6 flex-none cursor-pointer place-items-center rounded-[6px] border-0 bg-transparent text-muted hover:bg-[var(--hover)]"
        >
            <ChevronRight size={14} aria-hidden="true" className="transition-transform duration-150 motion-reduce:transition-none" style={{ transform: open ? 'rotate(90deg)' : undefined }} />
        </button>
    )
}

type TreeState = { open: Set<string>; setOpen: (id: string, open?: boolean) => void; activeId: string | undefined; badges: Badges }

const treeDomId = (id: string) => `cf-tree-${id.normalize('NFD').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`

/** A node of an experience tree: link to its screen (or, without one, an expander that opens its first visible
    descendant), chevron when it has branches, the branches indented under it (no guide lines; 29/10: lighter menu,
    lower rows and a smaller type on the deeper levels). Only the current screen gets the menta tint. */
function TreeNode({ node, state, depth = 0 }: { node: XpTreeItem; state: TreeState; depth?: number }) {
    const navigate = useNavigate()
    const { open, setOpen, activeId, badges } = state
    const active = node.id === activeId
    const hasKids = node.children.length > 0
    const isOpen = hasKids && open.has(node.id)
    const badge = isOpen ? (node.view ? (badges[node.view.key] ?? 0) : 0) : treeBadge(node, badges)
    const rowClass = cn('cf-ri flex min-h-7 min-w-0 flex-1 items-center gap-2 rounded-[7px] px-2 text-left no-underline transition-colors', depth === 0 ? 'text-[13.5px]' : 'text-[13px]', active ? 'font-medium' : 'hover:bg-[var(--hover)]')
    const rowStyle = active ? { background: 'var(--tint)', color: 'var(--text)' } : { color: 'var(--muted)' }
    const body = (
        <>
            <span className={cn('min-w-0 flex-1 truncate', active ? 'text-fg' : depth === 0 ? 'text-fg/85' : 'text-fg/70')}>{node.label}</span>
            {badge > 0 && <span className={BADGE}>{badge}</span>}
        </>
    )
    const openIt = () => {
        markFreshNav()
        if (hasKids) setOpen(node.id, true)
    }
    // tab nodes (Staff › Minha jornada…) carry ?tab= in `to`: TanStack takes it as `search`
    const link = splitPath(node.to)
    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-0.5">
                {node.view ? (
                    <Link to={link.to as '/'} search={link.search as never} onClick={openIt} aria-current={active ? 'page' : undefined} className={rowClass} style={rowStyle}>{body}</Link>
                ) : (
                    <button type="button" onClick={() => { openIt(); void navigate({ to: link.to as '/', search: link.search as never }) }} className={cn(rowClass, 'cursor-pointer border-0 bg-transparent')} style={rowStyle}>{body}</button>
                )}
                {hasKids && <Chevron open={isOpen} label={node.label} controls={treeDomId(node.id)} onClick={() => setOpen(node.id)} />}
            </div>
            {isOpen && (
                <div id={treeDomId(node.id)} className="flex flex-col pl-3">
                    {node.children.map(c => <TreeNode key={c.id} node={c} state={state} depth={depth + 1} />)}
                </div>
            )}
        </div>
    )
}

/** Experience item with a tree (expanded sidebar only): the item (opens the experience's first screen and its tree),
    chevron, and the tree nested under the guide line. Closed with the current screen inside: the item keeps the tint
    (always in the phone bar, where the tree is hidden: cf-tint-narrow). */
function TreeNavItem({ item, tree, pathname, badges }: { item: StaffItem; tree: XpTreeItem[]; pathname: string; badges: Badges }) {
    const { tab } = useSearch({ strict: false }) as { tab?: unknown }
    const activeIds = treeActivePath(tree, pathname, typeof tab === 'string' ? tab : null)
    const rootId = item.key
    const [open, setOpen] = useOpenNodes(activeIds.length ? [rootId, ...activeIds.slice(0, -1)] : [])
    const isOpen = open.has(rootId)
    const inside = item.active(pathname)
    const Icon = item.icon
    const state: TreeState = { open, setOpen, activeId: activeIds.at(-1), badges }
    const tinted = inside && !isOpen
    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-0.5">
                <Link
                    to={item.to as '/'}
                    onClick={() => { markFreshNav(); setOpen(rootId, true) }}
                    title={item.title}
                    aria-current={tinted ? 'page' : undefined}
                    className={cn('cf-ri relative flex min-h-9 min-w-0 flex-1 items-center gap-2.5 rounded-[10px] px-2.5 text-[14px] no-underline transition-colors', tinted ? 'font-medium' : 'hover:bg-[var(--hover)]', inside && 'cf-tint-narrow')}
                    style={tinted ? { background: 'var(--tint)', color: 'var(--text)' } : { color: 'var(--muted)' }}
                >
                    <span className="cf-tile relative grid size-6 flex-none place-items-center" style={{ color: inside ? 'var(--green)' : 'var(--muted)' }}>
                        <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                    </span>
                    <span className={cn('cf-only-wide min-w-0 flex-1 truncate', inside ? 'font-medium text-fg' : 'text-fg/90')}>{item.label}</span>
                    {!isOpen && !!item.badge && (
                        <span className={cn('cf-only-wide', BADGE)}>{item.badge}</span>
                    )}
                </Link>
                <Chevron open={isOpen} label={item.label} controls={treeDomId(rootId)} onClick={() => setOpen(rootId)} />
            </div>
            {isOpen && (
                <div id={treeDomId(rootId)} className="cf-only-wide flex flex-col pb-1 pl-[30px]">
                    {tree.map(node => <TreeNode key={node.id} node={node} state={state} />)}
                </div>
            )}
        </div>
    )
}

function NavItem({ item, active, collapsed, onClick }: { item: Item; active: boolean; collapsed: boolean; onClick?: () => void }) {
    const Icon = item.icon
    const name = item.badge ? `${item.label} (${item.badge})` : item.label
    return (
        <Link
            to={item.to as '/'}
            onClick={onClick}
            title={collapsed ? (item.title ?? item.label) : item.title}
            aria-label={collapsed ? (item.title ?? name) : undefined}
            aria-current={active ? 'page' : undefined}
            className={cn(
                'cf-ri relative flex min-h-9 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-[14px] no-underline transition-colors',
                collapsed && 'justify-center px-0',
                active ? 'font-medium' : 'hover:bg-[var(--hover)]',
            )}
            style={active ? { background: 'var(--tint)', color: 'var(--text)' } : { color: 'var(--muted)' }}
        >
            <span className="cf-tile relative grid size-6 flex-none place-items-center" style={{ color: active ? 'var(--green)' : 'var(--muted)' }}>
                <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                {item.live && <span aria-hidden="true" className="absolute -top-0.5 -right-0.5 size-[7px] rounded-full bg-green" style={{ boxShadow: '0 0 0 2px var(--panel)' }} />}
                {collapsed && !!item.badge && <span aria-hidden="true" className="absolute -top-1 -right-1.5 size-2 rounded-full bg-[var(--yellow)]" style={{ boxShadow: '0 0 0 2px var(--panel)' }} />}
            </span>
            <span className={cn('cf-only-wide min-w-0 flex-1 truncate', collapsed && 'hidden', active ? 'text-fg' : 'text-fg/90')}>{item.label}</span>
            {!collapsed && !!item.badge && (
                <span className={cn('cf-only-wide', BADGE)}>{item.badge}</span>
            )}
        </Link>
    )
}

function UserMenu({ me, detail, collapsed }: { me: Me; detail: string; collapsed: boolean }) {
    const [open, setOpen] = useState(false)
    const client = useQueryClient()
    const initial = me.name.trim().charAt(0).toUpperCase() || '?'
    const signOut = async () => {
        setOpen(false)
        try {
            await panelApi.logout()
        } finally {
            client.clear()
            window.location.assign('/')
        }
    }
    return (
        <div className="relative w-full">
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen(v => !v)}
                title={`${me.name} · ${detail}`}
                aria-label={`${me.name} · ${detail}`}
                className={cn('cf-avatar flex w-full cursor-pointer items-center gap-2.5 rounded-[12px] border-0 bg-transparent p-1.5 text-left hover:bg-[var(--hover)]', collapsed && 'justify-center')}
            >
                <span className="grid size-8 flex-none place-items-center rounded-full border border-line bg-raised text-[13px] font-semibold text-fg">{initial}</span>
                <span className={cn('cf-only-wide min-w-0 flex-1 flex-col leading-[1.25]', collapsed ? 'hidden' : 'flex')}>
                    <span className="truncate text-[13.5px] font-medium text-fg">{me.name}</span>
                    <span className="truncate text-[12px] text-muted">{detail}</span>
                </span>
                {!collapsed && <ChevronsUpDown size={16} className="cf-only-wide flex-none text-muted" aria-hidden="true" />}
            </button>
            <Popover open={open} onClose={() => setOpen(false)} className="bottom-full left-0 mb-1">
                <span className="flex flex-col px-2.5 pt-1 pb-1.5 leading-[1.3]">
                    <span className="text-[13.5px] font-medium">{me.name}</span>
                    <span className="text-[12px] text-muted">{me.email}</span>
                </span>
                <button type="button" role="menuitem" className={menuItem} onClick={() => void signOut()}>
                    <LogOut size={15} aria-hidden="true" />Sair
                </button>
            </Popover>
        </div>
    )
}

export function Sidebar({ me, workspace }: { me: Me; workspace: 'main' | 'admin' }) {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const [collapsed] = useSidebarCollapsed()
    const staff = workspace === 'admin'
    const overview = useQuery({ ...overviewQuery, enabled: !staff })
    const admin = useQuery(adminMeQuery).data
    const { frente, setFrente, frentes, nav, role, settings } = useFrenteNav()
    const badges = useRedeBadges(staff ? admin?.role ?? null : null, nav.map(e => e.view.key))
    const items = staff ? staffItems(nav, badges, role) : []
    const groups = staff ? [] : configMenu(overview.data?.pending.length ?? 0)
    const activeGroup = groups.find(g => g.items.length > 1 && g.items.some(i => isActive(pathname, i.to)))?.label ?? null
    const [openGroups, toggleGroup] = useOpenGroups(workspace, activeGroup)
    const reportActive = pathname === '/staff/denuncia'
    const groupLabel = cn('cf-only-wide px-2.5 pt-3 pb-1 text-[12px] font-medium text-muted', collapsed && 'hidden')
    const divider = cn('cf-only-wide mx-2 my-2 border-t border-line', !collapsed && 'hidden')
    const changeFrente = (f: FrenteKey) => {
        setFrente(f)
        markFreshNav()
        void navigate({ to: role ? frenteEntry(f, frenteNav(f, role, settings)) : '/staff' })
    }

    return (
        <aside
            aria-label={staff ? 'Menu Staff' : 'Menu Configurações'}
            className="cf-side z-20 self-stretch border-r border-line bg-panel transition-[flex-basis] duration-200"
            style={{ flex: `0 0 ${collapsed ? 72 : 248}px` }}
        >
            <div className="cf-side-in sticky top-0 flex h-dvh flex-col gap-1 overflow-y-auto px-3 py-3">
                <Brand workspace={workspace} collapsed={collapsed} canSwitch={admin?.role === 'admin'} />
                <SearchTrigger collapsed={collapsed} />

                <nav aria-label={staff ? 'Telas da frente' : 'Telas das Configurações'} className="cf-side-nav mt-1 flex w-full flex-col gap-0.5">
                    {staff && admin && (
                        <FrenteSwitcher frente={frente} frentes={frentes} onChange={changeFrente} collapsed={collapsed} />
                    )}
                    {staff && items.map(item => item.tree && !collapsed ? (
                        <TreeNavItem key={item.key} item={item} tree={item.tree} pathname={pathname} badges={badges} />
                    ) : (
                        <NavItem key={item.key} item={item} active={item.active(pathname)} collapsed={collapsed} onClick={markFreshNav} />
                    ))}
                    {staff && admin && items.length === 0 && !collapsed && (
                        <p className="cf-only-wide m-0 px-2.5 py-2 text-[12.5px] text-muted">Nenhuma tela liberada para esta frente. Peça à Tecnologia.</p>
                    )}
                    {!staff && (
                        <Link
                            to="/staff"
                            title="Voltar ao Staff (frentes de trabalho)"
                            aria-label={collapsed ? 'Voltar ao Staff' : undefined}
                            className={cn('cf-ri flex min-h-9 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-[14px] text-muted no-underline hover:bg-[var(--hover)]', collapsed && 'justify-center px-0')}
                        >
                            <span className="cf-tile grid size-6 flex-none place-items-center"><ArrowLeft size={18} strokeWidth={1.8} aria-hidden="true" /></span>
                            <span className={cn('cf-only-wide', collapsed && 'hidden')}>Voltar ao Staff</span>
                        </Link>
                    )}
                    {groups.map((g, i) => !collapsed && g.items.length > 1 ? (
                        <GroupSection key={g.label} group={g} open={openGroups.has(g.label)} onToggle={() => toggleGroup(g.label)} pathname={pathname} />
                    ) : (
                        <div key={g.label} role="group" aria-label={g.label} className="flex flex-col gap-0.5">
                            <span className={groupLabel}>{g.label}</span>
                            {i > 0 && <span aria-hidden="true" className={divider} />}
                            {g.items.map(item => <NavItem key={item.to} item={item} active={isActive(pathname, item.to)} collapsed={collapsed} />)}
                        </div>
                    ))}
                </nav>

                <div className="cf-side-foot mt-auto flex w-full flex-col gap-0.5 pt-2">
                    {staff && (
                        <Link
                            to="/staff/denuncia"
                            title="Canais de denúncia do ChildFund, inclusive anônimo"
                            aria-label={collapsed ? 'Canais de denúncia' : undefined}
                            aria-current={reportActive ? 'page' : undefined}
                            className={cn('cf-ri flex min-h-9 w-full items-center gap-2.5 rounded-[10px] px-2.5 text-[14px] no-underline hover:bg-[var(--hover)]', collapsed && 'justify-center px-0')}
                            style={{ color: 'var(--red-ink)', background: reportActive ? 'var(--red-bg)' : undefined }}
                        >
                            <span className="cf-tile grid size-6 flex-none place-items-center"><Flag size={18} strokeWidth={1.8} aria-hidden="true" /></span>
                            <span className={cn('cf-only-wide', collapsed && 'hidden')}>Denúncia</span>
                        </Link>
                    )}
                    <span aria-hidden="true" className="cf-only-wide mx-1 my-1 border-t border-line" />
                    <UserMenu me={me} detail={staff ? (admin?.roleLabel ?? me.email) : me.email} collapsed={collapsed} />
                </div>
            </div>
        </aside>
    )
}
